// pages/admin/TemplateDetail.tsx
/**
 * @description Template detail page - manage images for a template
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Loader2,
  ImageIcon,
  Trash2,
  Plus,
  GripVertical,
  Check
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Modal } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import {
  getBuilding,
  getTemplate,
  getImageLibrary,
  addTemplateImages,
  removeTemplateImage,
  type Building,
  type UnitTemplate,
  type TemplateImage,
  type ImageLibraryItem
} from '../../lib/api';

// Image type options for template images
const IMAGE_TYPE_OPTIONS = [
  { value: 'living_room', label: 'Living Room' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'bedroom', label: 'Bedroom' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'balcony', label: 'Balcony' },
  { value: 'floorplan', label: 'Floor Plan' },
  { value: 'other', label: 'Other' },
];

export function TemplateDetail() {
  const { slug, templateId } = useParams<{ slug: string; templateId: string }>();
  const { showToast } = useToast();

  // Data state
  const [building, setBuilding] = useState<Building | null>(null);
  const [template, setTemplate] = useState<UnitTemplate | null>(null);
  const [libraryImages, setLibraryImages] = useState<ImageLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedLibraryImages, setSelectedLibraryImages] = useState<Set<string>>(new Set());
  const [selectedImageType, setSelectedImageType] = useState('living_room');
  const [saving, setSaving] = useState(false);

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      if (!slug || !templateId) return;

      setLoading(true);
      setError(null);

      // Get building first
      const buildingResponse = await getBuilding(slug);
      if (buildingResponse.error) {
        setError(buildingResponse.error);
        setLoading(false);
        return;
      }

      if (buildingResponse.data) {
        setBuilding(buildingResponse.data.building);

        // Fetch template and library images in parallel
        const [templateResponse, libraryResponse] = await Promise.all([
          getTemplate(templateId),
          getImageLibrary(buildingResponse.data.building.id)
        ]);

        if (templateResponse.error) {
          setError(templateResponse.error);
        } else if (templateResponse.data) {
          setTemplate(templateResponse.data.template);
        }

        if (libraryResponse.data) {
          setLibraryImages(libraryResponse.data.images);
        }
      }

      setLoading(false);
    }

    fetchData();
  }, [slug, templateId]);

  // Handle adding images from library
  const handleAddFromLibrary = async () => {
    if (selectedLibraryImages.size === 0 || !template) return;

    setSaving(true);

    const imagesToAdd = Array.from(selectedLibraryImages).map((id) => {
      const img = libraryImages.find((i) => i.id === id);
      return {
        image_url: img?.image_url || '',
        image_type: selectedImageType,
      };
    }).filter((img) => img.image_url);

    const { data, error } = await addTemplateImages(template.id, imagesToAdd);
    setSaving(false);

    if (error) {
      showToast(`Failed to add images: ${error}`, 'error');
    } else if (data) {
      // Refresh template to get updated images
      const refreshed = await getTemplate(template.id);
      if (refreshed.data) {
        setTemplate(refreshed.data.template);
      }
      showToast(`Added ${data.ids.length} image(s)`, 'success');
      setSelectedLibraryImages(new Set());
      setIsPickerOpen(false);
    }
  };

  // Handle removing an image
  const handleRemoveImage = async (image: TemplateImage) => {
    if (!template) return;

    const { error } = await removeTemplateImage(template.id, image.id);

    if (error) {
      showToast(`Failed to remove image: ${error}`, 'error');
    } else {
      setTemplate((prev) => prev ? {
        ...prev,
        images: prev.images.filter((i) => i.id !== image.id)
      } : null);
      showToast('Image removed', 'success');
    }
  };

  // Toggle image selection in picker
  const toggleImageSelection = (id: string) => {
    setSelectedLibraryImages((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-gray-600">Loading template...</span>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error || !building || !template) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Template Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'Unable to load template data.'}</p>
          <Link to={`/admin/buildings/${slug}/templates`} className="text-primary hover:underline">
            ← Back to Templates
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Breadcrumb */}
        <div className="mb-4 lg:mb-6">
          <Link
            to={`/admin/buildings/${slug}/templates`}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Templates
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">
              {template.name}
            </h1>
            <p className="text-sm lg:text-base text-gray-500 mt-1">
              {building.name} • {template.unit_type?.name || 'No type'} • {template.images.length} image{template.images.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={() => setIsPickerOpen(true)} className="w-full sm:w-auto">
            <Plus size={16} className="mr-2" />
            Add Images
          </Button>
        </div>

        {/* Template Info */}
        {template.description && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">{template.description}</p>
          </div>
        )}

        {/* Images Grid */}
        {template.images.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <ImageIcon size={48} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Images Yet</h3>
            <p className="text-gray-500 mb-4">
              Add images from the building's image library to this template.
            </p>
            <Button onClick={() => setIsPickerOpen(true)}>
              <Plus size={16} className="mr-2" />
              Add Images
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {template.images.map((image, index) => (
              <div
                key={image.id}
                className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="aspect-square bg-gray-100">
                  <img
                    src={image.image_url}
                    alt={image.caption || `Image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {image.image_type.replace('_', ' ')}
                  </p>
                  {image.caption && (
                    <p className="text-xs text-gray-500 truncate">{image.caption}</p>
                  )}
                </div>
                {/* Delete button */}
                <button
                  onClick={() => handleRemoveImage(image)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  title="Remove image"
                >
                  <Trash2 size={14} />
                </button>
                {/* Drag handle (visual only for now) */}
                <div className="absolute top-2 left-2 p-1.5 bg-black/50 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                  <GripVertical size={14} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Picker Modal */}
      <Modal
        isOpen={isPickerOpen}
        onClose={() => {
          setIsPickerOpen(false);
          setSelectedLibraryImages(new Set());
        }}
        title="Add Images from Library"
        size="lg"
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-between gap-2 sm:gap-3">
            <div className="text-sm text-gray-500 self-center">
              {selectedLibraryImages.size} selected
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsPickerOpen(false);
                  setSelectedLibraryImages(new Set());
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddFromLibrary}
                disabled={selectedLibraryImages.size === 0 || saving}
              >
                {saving ? 'Adding...' : `Add ${selectedLibraryImages.size} Image${selectedLibraryImages.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Image Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image Type
            </label>
            <div className="flex flex-wrap gap-2">
              {IMAGE_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedImageType(opt.value)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    selectedImageType === opt.value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Library Images Grid */}
          {libraryImages.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500">No images in the library yet.</p>
              <p className="text-sm text-gray-400">
                Upload images to the building's image library first.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto">
              {libraryImages.map((img) => {
                const isSelected = selectedLibraryImages.has(img.id);
                const isAlreadyAdded = template.images.some((ti) => ti.image_url === img.image_url);

                return (
                  <button
                    key={img.id}
                    onClick={() => !isAlreadyAdded && toggleImageSelection(img.id)}
                    disabled={isAlreadyAdded}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      isAlreadyAdded
                        ? 'opacity-50 cursor-not-allowed border-gray-200'
                        : isSelected
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={img.image_url}
                      alt={img.filename}
                      className="w-full h-full object-cover"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      </div>
                    )}
                    {isAlreadyAdded && (
                      <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs py-1 text-center">
                        Already added
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </AdminLayout>
  );
}
